import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DateTimeInput } from 'semantic-ui-calendar-react';
import { FormField } from 'semantic-ui-react';
import styled from 'styled-components';
import { FarmersService } from '~/api/services/farmers';
import Drawer from '~/components/Drawer';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { LocationEl } from '~/pages/Dairy/DairyFactory/DairyFactoryProfile/Locations/style';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import { useAuth } from '~/routes/components/AuthProvider';
import { GlobalEl } from '~/styles/app';
import { Nullable } from '~/types';
import { handleErrors, parseDateWithTime } from '~/utils/helpers';

interface Props {
  noteId: Nullable<string>;
  handleHiddenDrawer: (submited?: boolean) => void;
}

const defaultValues = {
  userId: '',
  contactId: '',
  time: '',
  note: ''
}

export const StyledDateWrapper = styled(FormField)`
 .datePicker {
  margin-bottom: 3px !important;
 }
`

const CreateEditNote: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { noteId, handleHiddenDrawer } = props
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId);
  
  const [isBusy, setIsBusy] = useState(false)

  const { handleSubmit, control, reset, watch, setValue, getValues, formState: { isSubmitted } } = useForm({
    mode: 'onSubmit'
  })

  const watchTime = watch('time')
  const watchNote = watch('note')

  const {
    data: notesOptions,
    isLoading: isLoadingOptions
  } = FarmersQuery.getFarmerNotesOptions(id)

  useEffect(() => {
    if(noteId && noteId === 'create') {
      reset(defaultValues)
    } else if (noteId && noteId !== 'create') {
      fetchNoteData()
    }
  }, [noteId])

  async function fetchNoteData() {
    setIsBusy(true)
    try {
      const response = await FarmersService.getNote(id, noteId)
      response.data.time = dayjs(response.data.time).format('DD.MM.YYYY HH:mm')
      reset(response.data)
    }catch(error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderContent() {
    return <>
      <FormFields.Select
        label={t('Contacts')}
        placeholder={t('Select contact')}
        control={control}
        options={notesOptions?.contacts?.map(contact => ({ text: `${contact.firstName} ${contact.lastName}`, value: contact.id }))}
        onChange={(e, data) => {
          if(getValues('userId')) {
            setValue('userId', '')
          }
          setValue('contactId', data.value)
        }}
        search
        clearable
        noResultsMessage={t('No results found')}
        selectOnBlur={false}
        name='contactId'
      />
      <FormFields.Select
        label={t('Users')}
        placeholder={t('Select user')}
        control={control}
        options={notesOptions?.users?.map(user => ({ text: user.name, value: user.id }))}
        onChange={(e, data) => {
          if(getValues('contactId')) {
            setValue('contactId', '')
          }
          setValue('userId', data.value)
        }}
        search
        clearable
        selectOnBlur={false}
        name='userId'
      />
      <Controller
        control={control}
        name={'time'}
        rules={{
          required: {
            value: true,
            message: t('Please enter time')
          }
        }}
        render={({ field, fieldState }) => {
          return <StyledDateWrapper required error={!field.value && isSubmitted}>
            <label>{t('Time')}</label>
            <DateTimeInput
              name='dateTime'
              className={'datePicker'}
              maxDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
              placeholder={t('Date time')}
              closable
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              onKeyPress={(event) => event.preventDefault()}
              dateTimeFormat={'DD.MM.YYYY HH:mm'}
              value={watchTime || ''}
              iconPosition='left'
              onChange={(event, { name, value }) => setValue('time', value)}
            />
            {fieldState?.error?.message && !field.value && <div style={{ color: 'red', fontStyle: 'italic' }}>{fieldState.error.message}</div>}
          </StyledDateWrapper>
        }}
      />
      <FormFields.TextArea
        control={control}
        name='note'
        rules={{
          required: {
            value: true,
            message: t('Please enter note')
          },
          maxLength: {
            value: 1000,
            message: t('Max number of char. is 1000')
          }
        }}
        label={t('Note')}
        placeholder={t('Enter note')}
      />
      <small>{t('Characters: {{count}} (max 1000)', { count: watchNote?.length || 0 })}</small>
      <LocationEl.DrawerFooter>
        <GlobalEl.PrimaryButton
        >{noteId === 'create' ? t('Add') : t('Update')}</GlobalEl.PrimaryButton>
      </LocationEl.DrawerFooter>
    </>
  }

  async function onSubmit(data) {
    if(!data.userId && !data.contactId) {
      notify(t('Please select contact or user'), 'error')
      return
    }
    setIsBusy(true)
    try {
      data.time = parseDateWithTime(data.time)
      if(noteId === 'create') {
        await FarmersService.storeNote(id, data)
        notify(t('Successfully create note'), 'success')
      } else if (noteId && noteId !== 'create') {
        await FarmersService.updateNote(id, noteId, data)
        notify(t('Successfully updated note'), 'success')
      }
      handleHiddenDrawer(true)
    }catch(error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Drawer
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isLoadingOptions}
    header={noteId === 'create' ? t('Add new note') : t('Edit note')}
    content={renderContent()}
    isVisible={Boolean(noteId)}
    closeOnEscape
  />
}
export default CreateEditNote